import React, { useEffect, useRef, useState } from "react";
import { IoIosSearch, IoIosCloseCircle } from "react-icons/io";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { EventColumns, EventColumnsHeaders } from '../../SiteSummary/constants'
import { EventTable } from "../Table/EventTable";
import { v4 as uuidv4 } from 'uuid';
import { Popover, IconButton, Box, Button, Typography } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs, { Dayjs } from 'dayjs';



const calendarStyles = {
    backgroundColor: 'var(--Darkmode-700, #131F2E)',
    '& .MuiPickersDay-root': { color: 'white' },
    '& .MuiPickersDay-root:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: 'white !important',
    },
    '& .Mui-selected': {
        color: 'white !important',
        backgroundColor: 'rgba(0, 148, 189, 0.8) !important',
    },
    '& .Mui-selected:hover': {
        backgroundColor: 'rgba(0, 148, 189, 1) !important',
        color: 'white !important',
    },
    '& .MuiPickersDay-root.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        pointerEvents: 'none',
        borderRadius: '50%',
    },
    '& .MuiDayCalendar-header': { color: 'white' },
    '& .MuiPickersCalendarHeader-label': { color: 'white' },
    '& .MuiIconButton-root': { color: 'white' },
    '& .MuiTypography-root': { color: 'white' },
    '& .MuiDayPicker-weekDayLabel': { color: 'white' },
    '& .MuiIconButton-root.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.3)',
        pointerEvents: 'none',
    },
};

type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
interface SearchProps {
    tableData: any[];
    setTabDivHeight: SetTabPageFunction;
    addedRows: any[];
    setAddedRows: React.Dispatch<React.SetStateAction<any[]>>;
    editedComments: {};
    setEditedComments: React.Dispatch<React.SetStateAction<{}>>;
    isToggle: boolean;
    selectedOption: string;
    setSelectedOption: React.Dispatch<React.SetStateAction<string>>
    selectedRange: {
        start: Dayjs | null;
        end: Dayjs | null;
    };
    setSelectedRange: React.Dispatch<React.SetStateAction<{
        start: Dayjs | null;
        end: Dayjs | null;
    }>>;
    setClickApply: React.Dispatch<React.SetStateAction<boolean>>;
    showEventDataMessage: boolean;
}


const EventLogTable: React.FC<SearchProps> = ({ tableData, setTabDivHeight, addedRows, setAddedRows, editedComments, setEditedComments, isToggle, selectedOption, setSelectedOption, selectedRange, setSelectedRange, setClickApply, showEventDataMessage }) => {
    const [page, setPage] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [filterData, setFilterData] = useState<any[]>(tableData);
    const [currentPageNumber, setCurrentPageNumber] = useState(1)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 620); // Assuming width less than 768px is considered mobile
    const inputRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(dayjs());
    const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(dayjs());
    const open = Boolean(anchorEl);
    const id = open ? 'date-range-popover' : undefined;
    const [handleClickApply, setHandleClickApply] = useState(false);

    useEffect(() => {
        setTabDivHeight("1250px");
        setHandleClickApply(false)
    }, []);


    useEffect(() => {
        let timeoutId;

        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setIsMobile(window.innerWidth < 620); // Update state based on window width             
            }, 100); // Adjust the debounce interval as needed
        };

        window.addEventListener('resize', handleResize); // Add event listener for window resize

        return () => {
            window.removeEventListener('resize', handleResize); // Remove event listener on component unmount
        };
    }, [window.innerWidth]);


    useEffect(() => {
        setFilterData(tableData)
    }, [tableData])

    const handleAddRow = () => {
        const currentDate = new Date().toLocaleString('en-US', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,  // 24-hour format
        });


        const newRow = {
            eventId: uuidv4(),
            date: currentDate,
            type: "User Comments",
            source: localStorage.getItem("jwtUserEmail"),
            comment: "",
            change: "NA",
            added: true
        };

        // Add new row to the top of addedRows array
        setAddedRows([newRow, ...addedRows]);
        setFilterData([newRow, ...filterData])
    };


    const handleCalendarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setSelectedOption("Custom");  // Make "Custom" active when clicked
    };

    const handleClick = (label: string, event?: React.MouseEvent<HTMLElement>) => {
        if (label === "Custom") {
            if (event) handleCalendarClick(event);
        } else {
            setSelectedRange({ start: null, end: null })
            setTempStartDate(dayjs());
            setTempEndDate(dayjs());
            setSelectedOption(label);
            setHandleClickApply(false)
        }
        setPage(1)
    };

    const handleCancel = () => {
        if (handleClickApply) {
            setAnchorEl(null);
            return;
        }
        setTempStartDate(dayjs());
        setTempEndDate(dayjs());
        setAnchorEl(null);
    };

    const handleApply = () => {
        setClickApply(true)
        setHandleClickApply(true)
        setSelectedRange({ start: tempStartDate, end: tempEndDate });
        setTempStartDate(tempStartDate);
        setTempEndDate(tempEndDate);
        setAnchorEl(null);
    };
    const handleValueChange = (searchTerm: string) => {

        setSearchValue(searchTerm);
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const finalfilteredData = tableData?.filter((item) => {
            return Object.values(item).some((value) => {
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(lowerCaseSearchTerm);
                }
                return false;
            });
        });
        setPage(1)
        if (searchTerm === "") {
            setFilterData([...addedRows, ...tableData]);
        }
        else {
            setFilterData(finalfilteredData);
        }
    };


    const removeSearchText = () => {
        setSearchValue("");
        setFilterData([...addedRows, ...tableData]);
    };

    // Focus the input box initially and when searchValue is cleared
    useEffect(() => {
        handleFocus()
    }, [searchValue]);

    const handleFocus = () => {
        // Delay to avoid conflicts with other focus events        
        inputRef.current?.focus();
    };


    return (
        <>
            {" "}
            {/* missing grid area 1/2 */}
            <div
                className={`flex flex-1 max-w-full relative gap-[8px] ${
                    isMobile ? 'flex-col' : 'justify-between'
                }`}
                style={{ flexFlow: isMobile ? "column" : "row", minHeight: "32px" }}
            >
                {/* Search Box - full width on mobile, flex-[2] on desktop */}
                <div className={`flex items-center justify-between min-h-[40px] rounded-[6px] border border-darkcolor-400 bg-darkcolor-700 px-[14px] ${
                    isMobile ? 'w-full' : 'flex-[2]'
                }`}>
                    <div className="flex items-center gap-[8px] w-full">
                        <IoIosSearch
                            className="inline-block select-none align-text-bottom overflow-visible"
                            style={{ height: "2em", width: "1.5em" }}
                        />
                        <div className="relative flex items-center w-full h-full">
                            <div className="absolute top-1/2 left-0 transform -translate-y-1/2 text-[14px] whitespace-pre pointer-events-none text-white opacity-50">
                                {!searchValue && "Search"}
                            </div>
                            <input
                                ref={inputRef}
                                value={searchValue}
                                onChange={(e) => handleValueChange(e.target.value)}
                                onFocus={handleFocus}
                                onBlur={(e) => e.preventDefault()}
                                name="filter"
                                className="text-white border-0 bg-transparent p-0 w-full h-full outline-none caret-Darkmode-700 text-[14px]"
                            />
                            {searchValue && (
                                <button onClick={removeSearchText} className="absolute right-[4px] text-white">
                                    <IoIosCloseCircle size={20} className="bg-darkcolor-700" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right side: Time Options + Plus Button - second row on mobile */}
                <div className={`flex items-center gap-[8px] ${
                    isMobile ? 'justify-between w-full mt-[8px]' : 'shrink-0'
                }`}>
                    {/* 24 / All / Custom Buttons */}
                    <div className="flex items-center border border-darkcolor-400 bg-darkcolor-700 rounded-[6px] overflow-hidden shrink-0">
                        {["24", "All", "Custom"].map((label, index) => {
                            const isCustom = label === "Custom";
                            const isActive = selectedOption === label;

                            return (
                                <button
                                    key={label}
                                    onClick={(event) => handleClick(label, event)}
                                    className={`flex items-center justify-center gap-1 text-sm px-2 py-[2px] h-[40px] 
                                        ${isCustom ? 'w-[75px] sm:w-[90px]' : 'w-[55px] sm:w-[90px]'} text-xs sm:text-sm
              ${isActive ? "text-[var(--Primary-Indigo-500,#0094BD)]" : "text-white"}
              hover:text-[var(--Primary-Indigo-500,#0094BD)] 
              ${index !== 0 ? "border-l border-darkcolor-400" : ""}`}
                                >
                                    {isCustom && <CalendarMonthIcon fontSize="small" />}
                                    {label === "24" ? `${label}h` : label}
                                </button>
                            );
                        })}
                    </div>


                    {/* Plus Button */}
                    <button
                        onClick={handleAddRow}
                        className="py-0 px-[8px] h-[40px] w-[50px] gap-[4px] text-[14px] rounded-[6px] border-Darkmode-300 border-[1px] bg-Darkmode-700 focus:outline-none text-white"
                        style={{ borderColor: "rgb(74 84 99 / var(--tw-border-opacity))" }}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ height: "1.5em" }} />
                    </button>
                </div>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCancel}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: { xs: 300, sm: 500 }, // Responsive width
                                maxWidth: { xs: '90vw', sm: 'none' }, // Prevent overflow on mobile
                                backgroundColor: 'var(--Darkmode-700, #131F2E)',
                                color: 'white',
                                borderRadius: 2,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            },
                        }}
                    >
                        <Box display="flex" gap={2}
                                sx={{
                                    flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on mobile, horizontally on desktop
                                    minWidth: { xs: 300, sm: 500 } // Adjust width for mobile
                                }}
                        >
                            {/* Start Date Calendar */}
                            <DateCalendar
                                value={tempStartDate}
                                onChange={(newDate) => {
                                    setTempStartDate(newDate);
                                    // Always set end date to the newly selected start date
                                    setTempEndDate(newDate);
                                }}
                                // Also prevent start > end, but since end tracks start exactly, this is redundant:
                                // maxDate={tempEndDate}
                                sx={{
                                    ...calendarStyles
                                }}
                            />

                            <Box
                                sx={{
                                    // Conditional separator: horizontal line on mobile, vertical on desktop
                                    width: { xs: '100%', sm: '1px' },
                                    height: { xs: '1px', sm: 'auto' },
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    mx: { xs: 0, sm: 2 },
                                    my: { xs: 2, sm: 0 }
                                }}
                            />

                            {/* End Date Calendar */}
                            <DateCalendar
                                value={tempEndDate}
                                onChange={(newDate) => {
                                    // Prevent selecting before start date
                                    if (tempStartDate && newDate.isBefore(tempStartDate)) {
                                        setTempStartDate(newDate);
                                    }
                                    setTempEndDate(newDate);
                                }}
                                // End date cannot be before start date
                                minDate={tempStartDate || dayjs()}
                                maxDate={tempStartDate ? tempStartDate.add(30, 'day') : dayjs().add(30, 'day')}
                                sx={{
                                    ...calendarStyles
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                height: '1px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            }}
                        />
                        <p style={{ marginLeft: "12px", padding: "5px" }}>Selection allowed for max 30 days</p>

                        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">

                            <Typography variant="body2" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, px: 1, py: 1 }}>
                                    {tempStartDate?.format("MMM D, YYYY")}
                                </Box>
                                -
                                <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, px: 1, py: 1 }}>
                                    {tempEndDate?.format("MMM D, YYYY")}
                                </Box>
                            </Typography>

                            <Box display="flex" gap={1}>
                                <Button variant="outlined" onClick={handleCancel} sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={handleApply} sx={{ backgroundColor: '#0094BD', color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                    Apply
                                </Button>
                            </Box>
                        </Box>
                    </Popover>
                </LocalizationProvider>

            </div>


            <EventTable data={filterData} columns={EventColumns} columnHeaders={EventColumnsHeaders} defaultSortColumn="date" defaultSortOrder="desc" pagniation={true} page={page} setPage={setPage} searchValue={searchValue} addedRows={addedRows} setAddedRows={setAddedRows} handleAddRow={handleAddRow} setFilterData={setFilterData} editedComments={editedComments} setEditedComments={setEditedComments} isToggle={isToggle} showEventDataMessage={showEventDataMessage} />
            {/* {isMobile ? <SortTableMobile data={filterData} columns={SummaryColumns} defaultSortColumn="wellName" defaultSortOrder="asc" columnHeaders={SummaryColumnHeaders} pagniation={true} /> : <SortTable data={filterData} columns={SummaryColumns} defaultSortColumn="wellName" defaultSortOrder="asc" columnHeaders={SummaryColumnHeaders} pagniation={true} />} */}

        </>

    );
};

export default EventLogTable;
